import './KpiPicker.scss';
import cn from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { GetKpiPickerMapResponse } from '@blisspointmedia/bpm-types/dist/Performance';
import { Size, SizeType } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { Spinner } from '@Components/Spinner';
import { useAppDispatch, useAppSelector } from '../../redux';
import { fetchStreamingKpis, fetchYoutubeKpis, filteredKpisSelector } from '../../redux/company';
import { useSetError } from '../../redux/modals';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import {
  COMMERCE_DEFAULT_KPI,
  COMMERCE_KPI_MAP,
  MediaType,
  TYPE_DIMENSION
} from './kpi-picker-constants';
import {
  buildCrossChannelKpiMap,
  buildDropdownOptions,
  filterAndSortKpis,
  findMatchingKpi
} from './kpi-picker-utils';

interface UseKpiPickerDataArgs {
  company?: string;
  mediaType?: string;
  filter?: (kpi: string) => boolean;
  useCrossChannelKpis?: boolean;
  useTestParams?: boolean;
}

export const useKpiPickerData = ({
  company,
  mediaType,
  filter,
  useCrossChannelKpis = false,
  useTestParams = false
}: UseKpiPickerDataArgs): { kpiMap: GetKpiPickerMapResponse; defaultKpi: string } => {
  const { cid, kpis, initial_kpi, streaming_performance_default_kpi, cross_channel_kpis } =
    useCompanyInfo(company);

  const normalizedMediaType = (mediaType ?? '').toLowerCase();
  const streamingOnly = normalizedMediaType === MediaType.STREAMING;
  const youtubeOnly = normalizedMediaType === MediaType.YOUTUBE;
  const filteredKpis = useAppSelector(filteredKpisSelector);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (streamingOnly && !filteredKpis) {
      dispatch(fetchStreamingKpis(cid, mediaType, useTestParams));
    }
  }, [cid, dispatch, filteredKpis, mediaType, streamingOnly, useTestParams]);

  useEffect(() => {
    if (youtubeOnly && !filteredKpis) {
      dispatch(fetchYoutubeKpis(cid, useTestParams));
    }
  }, [cid, dispatch, filteredKpis, useTestParams, youtubeOnly]);

  const { kpiMap: processedKpiMap, firstKpi } = useMemo(() => {
    if (
      !kpis ||
      ((streamingOnly || youtubeOnly) && (!filteredKpis || filteredKpis === 'fetching'))
    ) {
      return { kpiMap: {}, firstKpi: '' };
    }
    return filterAndSortKpis(kpis, filteredKpis, streamingOnly || youtubeOnly, filter);
  }, [kpis, streamingOnly, filteredKpis, youtubeOnly, filter]);

  const defaultKpi = useMemo(() => {
    if (!Object.keys(processedKpiMap).length) {
      return '';
    }
    const candidate = streamingOnly ? streaming_performance_default_kpi : initial_kpi;
    return processedKpiMap[candidate] ? candidate : firstKpi;
  }, [processedKpiMap, initial_kpi, firstKpi, streamingOnly, streaming_performance_default_kpi]);

  if (useCrossChannelKpis) {
    return {
      kpiMap: buildCrossChannelKpiMap(cross_channel_kpis),
      defaultKpi: cross_channel_kpis[0] || ''
    };
  }
  if (normalizedMediaType === MediaType.COMMERCE) {
    return {
      kpiMap: COMMERCE_KPI_MAP,
      defaultKpi: COMMERCE_DEFAULT_KPI
    };
  }

  return { kpiMap: processedKpiMap, defaultKpi };
};

interface KpiPickerProps {
  className?: string;
  company?: string;
  defaultKpi?: string;
  disableTypeSelector?: boolean;
  filter?: (kpi: string) => boolean;
  kpi?: string;
  kpiMap?: GetKpiPickerMapResponse;
  mediaType?: string;
  noBreak?: boolean;
  onChange: (kpi: string) => void;
  size?: SizeType;
}

export const KpiPicker = ({
  className,
  company,
  defaultKpi: providedDefaultKpi,
  disableTypeSelector = false,
  filter,
  kpi: providedKpi,
  kpiMap: providedKpiMap,
  mediaType,
  noBreak = false,
  onChange,
  size = Size.LG
}: KpiPickerProps) => {
  const setError = useSetError();
  const { cid, kpi_filter_order } = useCompanyInfo(company);

  const [internalKpi, setInternalKpi] = useState<string>();
  const kpi = providedKpi ?? internalKpi;

  if (providedKpi && providedKpi !== internalKpi) {
    setInternalKpi(providedKpi);
  }

  const kpiPickerData = useKpiPickerData({ company: cid, mediaType, filter });
  const kpiMap = providedKpiMap ?? kpiPickerData.kpiMap;
  const defaultKpi = providedDefaultKpi ?? kpiPickerData.defaultKpi;

  if (!kpi && defaultKpi) {
    setInternalKpi(defaultKpi);
  }

  // Reset to a valid KPI if the provided one isn't in the current map.
  useEffect(() => {
    if (kpi && !kpiMap[kpi] && defaultKpi && Object.keys(kpiMap).length) {
      const message = `No info for KPI: "${kpi}". Falling back to "${defaultKpi}"`;
      setError({ message, reportError: new Error(message) });
      onChange(defaultKpi);
    }
  }, [kpiMap, kpi, setError, defaultKpi, onChange]);

  // Notify the parent of the resolved KPI when one wasn't provided.
  useEffect(() => {
    if (providedKpi == null && kpi && Object.keys(kpiMap).length && kpiMap[kpi]) {
      onChange(kpi);
    }
  }, [kpiMap, kpi, providedKpi, onChange]);

  const dropdownOptions = useMemo(() => {
    if (!Object.keys(kpiMap).length || !kpi) {
      return undefined;
    }
    return buildDropdownOptions(kpi, kpiMap, kpi_filter_order);
  }, [kpi, kpiMap, kpi_filter_order]);

  const onDropdownSelect = (dimension: string, value: string) => {
    const newKpi = findMatchingKpi(dimension, value, dropdownOptions ?? [], kpiMap, kpi ?? '');
    onChange(newKpi);
    setInternalKpi(newKpi);
  };

  if (!dropdownOptions) {
    return (
      <div className={cn('kpi-picker', { 'no-break': noBreak })}>
        <Spinner size={20} />
      </div>
    );
  }

  return (
    <div className={cn('kpi-picker', { 'no-break': noBreak })}>
      {dropdownOptions.map(({ dimension, value, options }) => {
        if (disableTypeSelector && dimension === TYPE_DIMENSION) {
          return null;
        }
        return (
          <KpiDimensionDropdown
            key={dimension}
            dimension={dimension}
            size={size}
            value={value}
            options={options}
            onSelect={onDropdownSelect}

            className={className}
          />
        );
      })}
    </div>
  );
};

interface KpiDimensionDropdownProps {
  className?: string;
  dimension: string;
  value: string;
  options: string[];
  onSelect: (dimension: string, value: string) => void;
  size: SizeType;
}

const KpiDimensionDropdown = ({
  className,
  dimension,
  value,
  options,
  onSelect,
  size
}: KpiDimensionDropdownProps) => (
  <Dropdown
    size={size}
    searchable
    value={value}
    options={options}
    label={dimension}

    className={className}
    onChange={selected => {
      if (selected) {
        onSelect(dimension, selected);
      }
    }}
  />
);
